/**
 * The list's filters <-> URL search params. Within a category the selected options are
 * alternatives (OR), categories combine with AND, the text search narrows further. Values are
 * normalized (known, unique, fixed order) so equal filters give equal URLs.
 */
import {
  EMPTY_TASKS_FILTER,
  type MyTasksFilter,
  TASK_BUCKETS,
  type TaskBucket,
} from '@/entities/my-tasks';

export interface ListFilter extends MyTasksFilter {
  /** Label names (case-insensitive) chosen client-side: ids differ from board to board. */
  labelNames: string[];
  assigneeIds: string[];
}

export const EMPTY_LIST_FILTER: ListFilter = {
  ...EMPTY_TASKS_FILTER,
  labelNames: [],
  assigneeIds: [],
};

const unique = (values: string[]) => [...new Set(values)].sort();

export function listFilterFromParams(params: URLSearchParams): ListFilter {
  const due = params.getAll('due');
  const status = params.getAll('status');
  return {
    q: params.get('q') ?? '',
    boardIds: unique(params.getAll('board')),
    workspaceIds: unique(params.getAll('ws')),
    // open + completed together = everything
    status:
      status.includes('open') && status.includes('completed')
        ? 'all'
        : status.includes('completed')
          ? 'completed'
          : 'open',
    buckets: TASK_BUCKETS.filter((bucket) => due.includes(bucket)),
    labelIds: [],
    labelNames: unique(params.getAll('label')),
    assigneeIds: unique(params.getAll('who')),
  };
}

export function listFilterToParams(filter: ListFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filter.q.trim()) params.set('q', filter.q.trim());
  for (const id of unique(filter.boardIds)) params.append('board', id);
  for (const id of unique(filter.workspaceIds)) params.append('ws', id);
  if (filter.status === 'completed') params.append('status', 'completed');
  if (filter.status === 'all') {
    params.append('status', 'open');
    params.append('status', 'completed');
  }
  for (const bucket of TASK_BUCKETS) {
    if (filter.buckets.includes(bucket)) params.append('due', bucket satisfies TaskBucket);
  }
  for (const name of unique(filter.labelNames)) params.append('label', name);
  for (const id of unique(filter.assigneeIds)) params.append('who', id);
  return params;
}

export function listFilterActive(filter: ListFilter): boolean {
  return (
    filter.q.trim() !== '' ||
    filter.boardIds.length > 0 ||
    filter.workspaceIds.length > 0 ||
    filter.status !== 'open' ||
    filter.buckets.length > 0 ||
    filter.labelNames.length > 0 ||
    filter.assigneeIds.length > 0
  );
}
